import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import { Loader2, User, Calendar, CheckCircle, Clock, XCircle, ExternalLink, Plus, Trash2 } from "lucide-react";
interface InfluencerProfile {
  id: string;
  name: string;
  category: string;
  image: string | null;
  approval_status: string | null;
  rejection_reason: string | null;
  rating: number;
  featured: boolean;
}

interface EventListing {
  id: string;
  name: string;
  category: string;
  image: string | null;
  approval_status: string | null;
  rejection_reason: string | null;
  company_name: string | null;
}

export const UserListingsSection = () => {
  const { language } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const isRTL = language === 'ar';

  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [influencerProfile, setInfluencerProfile] = useState<InfluencerProfile | null>(null);
  const [eventListings, setEventListings] = useState<EventListing[]>([]);

  useEffect(() => {
    const fetchListings = async () => {
      if (!user) return;

      try {
        setLoading(true);

        // Fetch influencer profile
        const { data: influencerData } = await supabase
          .from('influencers')
          .select('id, name, category, image, approval_status, rejection_reason, rating, featured')
          .eq('user_id', user.id)
          .maybeSingle();

        if (influencerData) {
          setInfluencerProfile(influencerData);
        }

        // Fetch event listings
        const { data: eventsData } = await supabase
          .from('event_organizers')
          .select('id, name, category, image, approval_status, rejection_reason, company_name')
          .eq('user_id', user.id);

        if (eventsData) {
          setEventListings(eventsData);
        }
      } catch (error) {
        console.error('Error fetching listings:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchListings();
  }, [user]);

  const handleDeleteInfluencer = async () => {
    if (!influencerProfile) return;
    
    setDeleting('influencer');
    try {
      const { error } = await supabase
        .from('influencers')
        .delete()
        .eq('id', influencerProfile.id);

      if (error) throw error;

      setInfluencerProfile(null);
      toast({
        title: isRTL ? "تم الحذف" : "Deleted",
        description: isRTL ? "تم حذف ملف المؤثر بنجاح" : "Influencer profile deleted successfully",
      });
    } catch (error) {
      console.error('Error deleting influencer:', error);
      toast({
        title: isRTL ? "خطأ" : "Error",
        description: isRTL ? "فشل في حذف الملف" : "Failed to delete profile",
        variant: "destructive",
      });
    } finally {
      setDeleting(null);
    }
  };

  const handleDeleteEvent = async (eventId: string) => {
    setDeleting(eventId);
    try {
      const { error } = await supabase
        .from('event_organizers')
        .delete()
        .eq('id', eventId);

      if (error) throw error;

      setEventListings(prev => prev.filter(e => e.id !== eventId));
      toast({
        title: isRTL ? "تم الحذف" : "Deleted",
        description: isRTL ? "تم حذف الفعالية بنجاح" : "Event listing deleted successfully",
      });
    } catch (error) {
      console.error('Error deleting event:', error);
      toast({
        title: isRTL ? "خطأ" : "Error",
        description: isRTL ? "فشل في حذف الفعالية" : "Failed to delete event listing",
        variant: "destructive",
      });
    } finally {
      setDeleting(null);
    }
  };

  const getStatusBadge = (status: string | null) => {
    switch (status) {
      case 'approved':
        return (
          <Badge variant="default" className="bg-green-500/10 text-green-600 border-green-500/20">
            <CheckCircle className="h-3 w-3 mr-1" />
            {isRTL ? "موافق عليه" : "Approved"}
          </Badge>
        );
      case 'rejected':
        return (
          <Badge variant="destructive" className="bg-red-500/10 text-red-600 border-red-500/20">
            <XCircle className="h-3 w-3 mr-1" />
            {isRTL ? "مرفوض" : "Rejected"}
          </Badge>
        );
      default:
        return (
          <Badge variant="secondary" className="bg-yellow-500/10 text-yellow-600 border-yellow-500/20">
            <Clock className="h-3 w-3 mr-1" />
            {isRTL ? "قيد المراجعة" : "Pending"}
          </Badge>
        );
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Don't show section if user has no listings
  if (!influencerProfile && eventListings.length === 0) {
    return null;
  }

  return (
    <div className="space-y-6 mb-8">
      <h2 className="text-xl font-semibold">
        {isRTL ? "قوائمي" : "My Listings"}
      </h2>

      <div className="grid gap-4 md:grid-cols-2">
        {/* Influencer Profile Card */}
        {influencerProfile && (
          <Card className="overflow-hidden" animated={true}>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <User className="h-5 w-5 text-primary" />
                  <CardTitle className="text-lg">
                    {isRTL ? "ملف المؤثر" : "Influencer Profile"}
                  </CardTitle>
                </div>
                {getStatusBadge(influencerProfile.approval_status)}
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4">
                <Avatar className="h-16 w-16">
                  <AvatarImage src={influencerProfile.image || undefined} />
                  <AvatarFallback className="text-lg">
                    {influencerProfile.name?.charAt(0) || 'I'}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold truncate">{influencerProfile.name}</h3>
                  <p className="text-sm text-muted-foreground capitalize">{influencerProfile.category}</p>
                  {influencerProfile.featured && (
                    <Badge variant="outline" className="mt-1 text-xs">
                      {isRTL ? "مميز" : "Featured"}
                    </Badge>
                  )}
                </div>
              </div>
              {influencerProfile.approval_status === 'rejected' && influencerProfile.rejection_reason && (
                <p className="text-sm text-destructive mt-3 p-2 bg-destructive/5 rounded">
                  {isRTL ? "سبب الرفض:" : "Reason:"} {influencerProfile.rejection_reason}
                </p>
              )}
              <div className="flex gap-2 mt-4">
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="flex-1"
                  onClick={() => navigate('/influencer-dashboard')}
                >
                  <ExternalLink className="h-4 w-4 mr-2" />
                  {isRTL ? "عرض لوحة التحكم" : "View Dashboard"}
                </Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button 
                      variant="outline" 
                      size="sm"
                      className="text-destructive hover:text-destructive hover:bg-destructive/10"
                      disabled={deleting === 'influencer'}
                    >
                      {deleting === 'influencer' ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Trash2 className="h-4 w-4" />
                      )}
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>
                        {isRTL ? "حذف ملف المؤثر" : "Delete Influencer Profile"}
                      </AlertDialogTitle>
                      <AlertDialogDescription>
                        {isRTL 
                          ? "هل أنت متأكد من حذف ملف المؤثر الخاص بك؟ لا يمكن التراجع عن هذا الإجراء."
                          : "Are you sure you want to delete your influencer profile? This action cannot be undone."}
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>{isRTL ? "إلغاء" : "Cancel"}</AlertDialogCancel>
                      <AlertDialogAction 
                        onClick={handleDeleteInfluencer}
                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                      >
                        {isRTL ? "حذف" : "Delete"}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Event Listings Cards */}
        {eventListings.map((event) => (
          <Card key={event.id} className="overflow-hidden" animated={true}>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-primary" />
                  <CardTitle className="text-lg">
                    {isRTL ? "فعالية" : "Event Listing"}
                  </CardTitle>
                </div>
                {getStatusBadge(event.approval_status)}
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4">
                <Avatar className="h-16 w-16 rounded-lg">
                  <AvatarImage src={event.image || undefined} className="rounded-lg" />
                  <AvatarFallback className="text-lg rounded-lg">
                    {event.name?.charAt(0) || 'E'}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold truncate">{event.name}</h3>
                  <p className="text-sm text-muted-foreground capitalize">{event.category}</p>
                  {event.company_name && (
                    <p className="text-xs text-muted-foreground mt-1">{event.company_name}</p>
                  )}
                </div>
              </div>
              {event.approval_status === 'rejected' && event.rejection_reason && (
                <p className="text-sm text-destructive mt-3 p-2 bg-destructive/5 rounded">
                  {isRTL ? "سبب الرفض:" : "Reason:"} {event.rejection_reason}
                </p>
              )}
              <div className="flex justify-end mt-4">
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button 
                      variant="outline" 
                      size="sm"
                      className="text-destructive hover:text-destructive hover:bg-destructive/10"
                      disabled={deleting === event.id}
                    >
                      {deleting === event.id ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      ) : (
                        <Trash2 className="h-4 w-4 mr-2" />
                      )}
                      {isRTL ? "حذف" : "Delete"}
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>
                        {isRTL ? "حذف الفعالية" : "Delete Event Listing"}
                      </AlertDialogTitle>
                      <AlertDialogDescription>
                        {isRTL 
                          ? `هل أنت متأكد من حذف "${event.name}"؟ لا يمكن التراجع عن هذا الإجراء.`
                          : `Are you sure you want to delete "${event.name}"? This action cannot be undone.`}
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>{isRTL ? "إلغاء" : "Cancel"}</AlertDialogCancel>
                      <AlertDialogAction 
                        onClick={() => handleDeleteEvent(event.id)}
                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                      >
                        {isRTL ? "حذف" : "Delete"}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="flex flex-wrap gap-3">
        {!influencerProfile && (
          <Button variant="outline" size="sm" onClick={() => navigate('/influencer-signup')}>
            <Plus className="h-4 w-4 mr-2" />
            {isRTL ? "سجل كمؤثر" : "Become an Influencer"}
          </Button>
        )}
        <Button variant="outline" size="sm" onClick={() => navigate('/event-listing-signup')}>
          <Plus className="h-4 w-4 mr-2" />
          {isRTL ? "أضف فعالية جديدة" : "List a New Event"}
        </Button>
      </div>
    </div>
  );
};
